import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listUsers, UserListItem } from '../../api/admin';
import './Admin.css';

const formatGrade = (grade: number | null): string => {
  if (grade === null) return '-';
  if (grade === 0) return 'K';
  return grade.toString();
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers(page, pageSize, search || undefined);
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div>
      <div className="admin-page-header">
        <h2>Users</h2>
      </div>

      <form className="admin-search" onSubmit={handleSearch}>
        <input
          type="text"
          className="admin-search-input"
          placeholder="Search by username..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="admin-btn admin-btn-primary">
          Search
        </button>
        {search && (
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPage(1);
            }}
          >
            Clear
          </button>
        )}
      </form>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-table-container">
        {loading ? (
          <div className="admin-empty">
            <p>Loading...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="admin-empty">
            <p>No users found</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Grade</th>
                  <th>Coins</th>
                  <th>Characters</th>
                  <th>Admin</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>{formatGrade(user.grade)}</td>
                    <td>{user.coins}</td>
                    <td>{user.characters_count}</td>
                    <td>
                      {user.is_admin ? (
                        <span className="admin-badge admin-badge-success">Yes</span>
                      ) : (
                        <span className="admin-badge admin-badge-info">No</span>
                      )}
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="admin-btn admin-btn-primary admin-btn-small"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="admin-pagination">
              <span className="admin-pagination-info">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="admin-pagination-buttons">
                <button
                  className="admin-btn admin-btn-secondary admin-btn-small"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="admin-btn admin-btn-secondary admin-btn-small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
